/**
 * React Query hooks for the `/api/onboarding` surface (WP-1 backend contract)
 * plus the connector/spec helpers the wizard composes.
 *
 * The onboarding endpoints speak snake_case on the wire (per the design doc),
 * so responses are run through {@link camelizeKeys} and request bodies through
 * {@link snakeizeKeys} — same boundary treatment as the data-tables surface.
 *
 * Smart polling mirrors `knowledge/api/jobs.ts`: the overview query refetches
 * every 5s while any source in any group is still `connecting`, then idles.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "../../agents/api/client";
import { camelizeKeys, snakeizeKeys } from "../../tables/api/camelize";

export type SurfaceMode = "full" | "onboarding";
export type OnboardingState = "collecting" | "submitted" | "building";
export type GroupKey =
  | "catalog"
  | "payments"
  | "orders"
  | "crm"
  | "knowledge"
  | "api"
  | "marketing";
export type SourceKind = "kb_source" | "connection" | "api_spec" | "tool_connection";
export type SourceStatus = "needed" | "connecting" | "connected" | "action_needed" | "error";

export interface OverviewSource {
  kind: SourceKind;
  id: string;
  name: string;
  status: SourceStatus;
  detail: string | null;
}

export interface OverviewGroup {
  key: GroupKey;
  required: boolean;
  sources: OverviewSource[];
}

export interface PendingInvitation {
  email: string;
  createdAt: string;
}

export interface OnboardingOverview {
  surfaceMode: SurfaceMode;
  onboardingState: OnboardingState;
  groups: OverviewGroup[];
  pendingInvitations: PendingInvitation[];
}

export const onboardingKeys = {
  overview: (slug: string) => ["onboarding", slug, "overview"] as const,
};

function hasConnecting(o: OnboardingOverview | undefined): boolean {
  return Boolean(o?.groups.some((g) => g.sources.some((s) => s.status === "connecting")));
}

export function useOnboardingOverview(slug: string) {
  return useQuery({
    queryKey: onboardingKeys.overview(slug),
    queryFn: () =>
      apiRequest<unknown>("GET", "/api/onboarding/overview").then(camelizeKeys<OnboardingOverview>),
    refetchInterval: (q) => {
      const data = q.state.data as OnboardingOverview | undefined;
      return hasConnecting(data) ? 5_000 : false;
    },
  });
}

export function useSubmitOnboarding(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<{ onboarding_state: OnboardingState }>("POST", "/api/onboarding/submit").then(
        (r) => r.onboarding_state,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onboardingKeys.overview(slug) });
    },
  });
}

// ----- validate-db (WP-2) — may not be on this branch; callers treat a 404
// (AgentsApiError.status === 404) as "live testing unavailable". -----

export interface DbValidationRequest {
  engine: "postgres" | "mysql" | "mongodb";
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  ssl?: boolean;
}

export interface DbSchemaEntry {
  name: string;
  rowEstimate?: number;
}

export interface DbValidationResult {
  ok: boolean;
  errorCode?: "DNS" | "SSRF_BLOCKED" | "CONNECT_TIMEOUT" | "AUTH_FAILED" | "TLS" | "UNKNOWN";
  errorMessage?: string;
  schema?: DbSchemaEntry[];
}

export function useValidateDb() {
  return useMutation({
    // `credentials` aren't snakeized as a blob here — the validate-db body is a
    // flat snake_case object, so the whole request is transformed.
    mutationFn: (body: DbValidationRequest) =>
      apiRequest<unknown>("POST", "/api/onboarding/validate-db", snakeizeKeys(body)).then(
        camelizeKeys<DbValidationResult>,
      ),
  });
}

// ----- API-spec import (WP-4) — guarded the same way as validate-db. -----

export interface SpecValidateRequest {
  name: string;
  specText?: string;
  specUrl?: string;
  auth?: { headerName: string; headerValue: string };
}

export interface SpecEndpointPreview {
  operationId: string;
  method: string;
  pathTemplate: string;
  summary: string;
}

export interface SpecValidateResult {
  endpoints: SpecEndpointPreview[];
  warnings: string[];
}

export interface SpecImportResult {
  id: string;
  name: string;
  endpointCount: number;
  skillCount: number;
}

export function useValidateSpec() {
  return useMutation({
    mutationFn: (body: SpecValidateRequest) =>
      apiRequest<unknown>("POST", "/api/api-specs/validate", snakeizeKeys(body)).then(
        camelizeKeys<SpecValidateResult>,
      ),
  });
}

export function useImportSpec(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SpecValidateRequest) =>
      apiRequest<unknown>("POST", "/api/api-specs", snakeizeKeys(body)).then(
        camelizeKeys<SpecImportResult>,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onboardingKeys.overview(slug) });
    },
  });
}
